import * as React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import moment from 'moment'

import { TIME_FORMAT } from 'app/constants/date'
import { GroupModel } from 'app/models/groupModel'
import { NewsModel } from 'app/models/newsModel'
import { Dropdown } from '@/ui/form/controls/Dropdown'
import {
  NEWS_KEY,
  addMessageRequest,
  editMessageRequest,
} from 'app/containers/administration/newsQueries'

import { AdminDateField, CheckboxField } from '@/modules/shared/components'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { toast } from '@/lib/toasts/api'
import { Button } from '@/ui/button'

/** Kolory statusu do wyboru; pierwszy jest domyślny dla nowego komunikatu. */
const STATUSES = [
  '#459A18',
  '#FFA23F',
  '#FF515A',
  '#D67FFF',
  '#7FC9FF',
  '#808080',
]

type Props = {
  editedMessage: NewsModel
  groups: GroupModel[]
  onClose: (saved: boolean) => void
}

/**
 * Formularz komunikatu - funkcyjny odpowiednik
 * `app/components/administration/editMessage.tsx`.
 *
 * Ten sam formularz dodaje i edytuje: komunikat bez `id` idzie na `create`.
 * Zapis unieważnia klucz listy, więc zakładka pod spodem odświeża się sama.
 */
export const EditMessageForm: React.FC<Props> = ({
  editedMessage,
  groups,
  onClose,
}) => {
  const { t } = useLocalize()
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (message: NewsModel) =>
      message.id ? editMessageRequest(message) : addMessageRequest(message),
  })

  const [message, setMessage] = React.useState<NewsModel>(editedMessage)
  const [dateToSpecified, setDateToSpecified] = React.useState(
    editedMessage.endDate != null,
  )
  const [forGroup, setForGroup] = React.useState(
    editedMessage.groups != null && editedMessage.groups.length > 0,
  )

  const patch = (changes: Partial<NewsModel>) =>
    setMessage((prev) => ({ ...prev, ...changes }))

  const save = async () => {
    await mutation.mutateAsync({
      id: message.id,
      text: message.text,
      status: message.status,
      deleted: message.deleted ?? false,
      groups: message.groups,
      startDate: moment(message.startDate),
      endDate: message.endDate != null ? moment(message.endDate) : null,
    } as NewsModel)
    await queryClient.invalidateQueries({ queryKey: NEWS_KEY })

    toast.success({
      title: t('administrationPanel-messages.message-edited-title'),
      text: t('administrationPanel-messages.message-edited-details').replace(
        '{0}',
        moment(message.startDate).format(TIME_FORMAT),
      ),
    })
    onClose(true)
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <div className="tw-flex tw-flex-col tw-gap-1">
        <span className="tw-text-sm tw-font-medium tw-text-gray-700">
          {t('administrationPanel-messages.text')}
        </span>
        <textarea
          className="tw-h-32 tw-w-full tw-resize-none tw-rounded-md tw-border tw-border-gray-300 tw-p-2 tw-text-sm"
          value={message.text ?? ''}
          onChange={(event) => patch({ text: event.target.value })}
        />
      </div>

      <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-4">
        <div className="tw-flex tw-flex-col tw-gap-1">
          <span className="tw-text-sm tw-font-medium tw-text-gray-700">
            {t('administrationPanel-messages.valid')}
          </span>
          <div className="tw-flex tw-items-center tw-gap-2">
            <div className="tw-w-[190px]">
              <AdminDateField
                withTime
                value={message.startDate}
                onChange={(date) => date && patch({ startDate: moment(date) })}
              />
            </div>
            <CheckboxField
              id="dateToSpecified"
              checked={dateToSpecified}
              label=""
              onChange={() => {
                const next = !dateToSpecified
                setDateToSpecified(next)
                patch({ endDate: next ? moment(message.startDate) : null })
              }}
            />
            <div className="tw-w-[190px]">
              <AdminDateField
                withTime
                disabled={!dateToSpecified}
                value={message.endDate}
                onChange={(date) => date && patch({ endDate: moment(date) })}
              />
            </div>
          </div>
        </div>

        <div className="tw-flex tw-flex-col tw-gap-1">
          <span className="tw-text-sm tw-font-medium tw-text-gray-700">
            {t('administrationPanel-messages.status')}
          </span>
          <div className="tw-w-[110px]">
            <Dropdown
              compact
              hideArrow={false}
              placeholder=""
              value={message.status ?? null}
              onChange={(status: string) => patch({ status })}
              options={STATUSES.map((color) => ({
                value: color,
                label: color,
                content: (
                  <span
                    className="tw-block tw-h-5 tw-w-6 tw-rounded-sm"
                    style={{ background: color }}
                  />
                ),
              }))}
            />
          </div>
        </div>
      </div>

      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-4">
        <CheckboxField
          id="forGroup"
          checked={forGroup}
          label={t('administrationPanel-messages.for-group')}
          onChange={() => {
            const next = !forGroup
            setForGroup(next)
            if (!next) patch({ groups: [] })
          }}
        />
        {forGroup && (
          <div className="tw-w-[300px]">
            <Dropdown
              compact
              multiple
              showSelectAll
              placeholder={t('administrationPanel-messages.choose-group')}
              options={groups.map((group) => ({
                value: String(group.id),
                label: group.name,
              }))}
              value={(message.groups ?? []).map(String)}
              onChange={(next: string[]) => patch({ groups: next.map(Number) })}
            />
          </div>
        )}
        <CheckboxField
          id="isDeleted"
          checked={!!message.deleted}
          label={t('administrationPanel-messages.deleted')}
          onChange={() => patch({ deleted: !message.deleted })}
        />
      </div>

      <div className="tw-flex tw-justify-end tw-gap-2">
        <Button
          variant="primary"
          mode="outline"
          size="sm"
          onClick={() => onClose(false)}
        >
          {t('operation.cancel')}
        </Button>
        <Button variant="primary" size="sm" onClick={save}>
          {t('operation.save')}
        </Button>
      </div>
    </div>
  )
}
